import { useMemo, useState } from "react";
import UploadIcon from "@assets/images/NoSubscriptionAstronot.png";
import { getCheckoutLink, getPendingPaymentUrl } from "@api/authService";
import { useUserData } from "@context/OAuth/AuthContext";
import { toast } from "react-toastify";
import ButtonLoader from "./Loaders/ButtonLoader";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return format(parseISO(dateStr), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

const SubscriptionPending = () => {
  const { subscription } = useUserData();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const trialDaysLeft = useMemo(() => {
    const endDate = subscription?.trial_end || subscription?.next_payment_date;
    if (!endDate) return null;
    const diff = Math.ceil(
      (new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    return diff > 0 ? diff : 0;
  }, [subscription?.trial_end, subscription?.next_payment_date]);

  const handlePayNow = async () => {
    if (!subscription?.subscription_id) {
      toast.error(t("subscription.checkoutUnavailable"));
      return;
    }

    try {
      setLoading(true);
      let response = null;
      if(subscription?.status === "on-hold"){
        response = await getCheckoutLink(subscription?.subscription_id);
      } else {
        response = await getPendingPaymentUrl(subscription?.subscription_id);
      }
    if (response && response.payment_url) {
      // 3. Use standard browser navigation for external links
      window.location.href = response.payment_url;
    } else {
      toast.error("Fialed to get a valid payment url . Please contact to support");
    }
    } catch (error) {
      console.error("Error getting pending payment url:", error);
      toast.error(t("subscription.checkoutUnavailable"));
    } finally {
      setLoading(false);
    }
  };

  const detailRows = [
    {
      label: t("subscription.status"),
      value: t("subscription.statusPending"),
    },
    {
      label: t("subscription.startDate"),
      value: formatDate(subscription?.start_date),
    },
    subscription?.is_trial
      ? {
          label: t("subscription.trialEnds"),
          value: formatDate(subscription?.trial_end),
        }
      : null,
    {
      label: t("subscription.nextPayment"),
      value: formatDate(subscription?.next_payment_date),
    },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-6 bg-white min-h-[587px] rounded-[12px] flex flex-col justify-center items-center">
      <img
        src={UploadIcon}
        className="border border-white rounded-full p-1"
        alt=""
      />

      <h2 className="text-[24px] font-[700] text-[#303030] text-center font-Lato mt-4">
        {t("subscription.paymentPendingTitle")}
      </h2>

      <p className="text-[#6C6C6C] text-[16px] font-[Lato] text-center my-2 max-w-[520px]">
        {t("subscription.paymentPendingDescription")}
      </p>

      {subscription?.is_trial && trialDaysLeft !== null && (
        <p className="text-[#0575E6] text-[14px] font-[500] font-Lato text-center mb-4">
          {t("subscription.pendingRenewalMessage", { days: trialDaysLeft })}
        </p>
      )}

      <div className="w-full max-w-[420px] rounded-[12px] border border-[#E8E8E8] bg-[#FAFAFA] p-4 mb-6">
        {detailRows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between py-2 border-b border-[#EEEEEE] last:border-b-0"
          >
            <span className="text-[14px] text-[#6C6C6C] font-[Lato]">
              {row.label}
            </span>
            <span className="text-[14px] text-[#303030] font-[600] font-[Lato]">
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="px-[18px] py-[10px] bg-[#64DDFF] text-white rounded-[32px] flex items-center justify-center gap-2 disabled:opacity-80 min-w-[180px]"
        disabled={loading}
        onClick={handlePayNow}
      >
        {loading ? <ButtonLoader /> : t("subscription.payNow")}
      </button>
    </div>
  );
};

export default SubscriptionPending;
